import { RefObject, useRef, useState, type FC } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  FloatingArrow,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useInteractions
} from "@floating-ui/react";

interface ConversationStarterButtonProps {
  containerRef: RefObject<HTMLDivElement>;
  title: string;
  description: string;
  onClick: () => void;
}

const ConversationStarterButton: FC<ConversationStarterButtonProps> = ({ containerRef, title, description, onClick }) => {
  const arrowRef = useRef<SVGSVGElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const tooltipMaxWidth = (containerRef.current?.offsetWidth ?? 0) - 26;

  const {refs, floatingStyles, context} = useFloating({
    placement: "top",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(14),
      shift({
        boundary: containerRef.current ?? undefined,
        padding: 12
      }),
      arrow({ element: arrowRef })
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {move: false});
  const focus = useFocus(context);
  const dismiss = useDismiss(context);

  const {getReferenceProps, getFloatingProps} = useInteractions([hover, focus, dismiss]);

  return (
    <div className={`relative shrink min-w-0 z-10`}>
      <button
        type="button"
        ref={refs.setReference}
        className="bg-lastbot-surface-muted rounded-full px-2.5 py-1 text-lastbot-on-surface-soft text-xs font-medium cursor-pointer truncate w-full"
        data-testid="conversation-starter"
        onClick={onClick}
        {...getReferenceProps()}
      >
        {title}
      </button>
      <div
        ref={refs.setFloating}
        style={{...floatingStyles, maxWidth: Math.max(0, tooltipMaxWidth)}}
        className={`w-max text-xs text-lastbot-border font-semibold bg-lastbot-surface-inverse rounded px-2.5 py-1.5 duration-200 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
        {...getFloatingProps()}
      >
        {description}
        <FloatingArrow ref={arrowRef} context={context} />
      </div>
    </div>
  );
};

export default ConversationStarterButton;
